import React from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import classnames from 'classnames'

export const LIST_SEARCH_KEY = 'tariffs.listSearch'

const SECTION_TABS = [
    { id: 'tariffs', label: 'Tariffs', path: '/admin/tariffs' },
    { id: 'rate-validation', label: 'Rate validation', path: '/admin/tariffs/rate-validation' },
    { id: 'acc-rates', label: 'NEM3 export rates', path: '/admin/tariffs/acc-rates' },
] as const

function tariffsListHref(): string {
    try {
        const stored = sessionStorage.getItem(LIST_SEARCH_KEY)
        return stored != null && stored !== '' ? `/admin/tariffs?${stored}` : '/admin/tariffs'
    } catch {
        return '/admin/tariffs'
    }
}

function sectionTabId(pathname: string): (typeof SECTION_TABS)[number]['id'] | null {
    if (pathname === '/admin/tariffs/rate-validation') return 'rate-validation'
    if (pathname === '/admin/tariffs/acc-rates') return 'acc-rates'
    if (pathname === '/admin/tariffs' || pathname === '/admin/tariffs/') return 'tariffs'
    return null
}

export const TariffSectionTabs: React.FC = () => {
    const { pathname } = useLocation()
    const active = sectionTabId(pathname)

    return (
        <nav aria-label="Tariff pages" className="flex min-w-0 gap-x-6 overflow-x-auto">
            {SECTION_TABS.map((tab) => {
                const to = tab.id === 'tariffs' ? tariffsListHref() : tab.path
                const current = active === tab.id
                return (
                    <NavLink
                        key={tab.id}
                        to={to}
                        end={tab.id === 'tariffs'}
                        className={classnames(
                            'whitespace-nowrap border-b-2 px-1 py-2.5 text-sm font-medium',
                            current
                                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-700 dark:hover:text-slate-300'
                        )}
                    >
                        {tab.label}
                    </NavLink>
                )
            })}
        </nav>
    )
}

export const BackToTariffsLink: React.FC<{ to?: string; compact?: boolean }> = ({
    to = '/admin/tariffs',
    compact = false,
}) => (
    <Link
        to={to}
        className={classnames(
            'inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300',
            compact ? 'text-sm' : 'text-base font-medium'
        )}
    >
        <ArrowLeftIcon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        <span>Back to Tariffs</span>
    </Link>
)

export const TariffPageHeader: React.FC<{
    title?: React.ReactNode
    /** Compact back link above the title on standalone tariff pages. */
    backLink?: React.ReactNode
    /** Underline tabs for Tariffs / Rate validation / NEM3 export rates. */
    showSectionTabs?: boolean
    /** Optional line under the title (rarely used on list; detail uses sticky header instead). */
    subtitle?: React.ReactNode
    actions?: React.ReactNode
    /** Leave room for the fixed NotesDrawer FAB (right-6, w-12, badge). */
    reserveNotesFab?: boolean
    className?: string
}> = ({ title, backLink, showSectionTabs = false, subtitle, actions, reserveNotesFab = false, className }) => {
    const resolvedTitle = title === undefined && !showSectionTabs ? 'Tariffs' : title

    return (
        <div className={classnames('mb-4', className)}>
            {showSectionTabs ? (
                <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-b border-slate-200 dark:border-slate-700">
                    <TariffSectionTabs />
                    {actions != null ? (
                        <div
                            className={classnames(
                                'flex shrink-0 items-center gap-2 pb-2',
                                reserveNotesFab && 'mr-24 sm:mr-28'
                            )}
                        >
                            {actions}
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                        {backLink != null ? <div className="mb-2">{backLink}</div> : null}
                        {typeof resolvedTitle === 'string' ? (
                            <h1 className="m-0 text-2xl font-medium leading-none text-slate-900 dark:text-white">
                                {resolvedTitle}
                            </h1>
                        ) : resolvedTitle != null ? (
                            <div className="text-slate-900 dark:text-white">{resolvedTitle}</div>
                        ) : null}
                    </div>
                    {actions != null ? (
                        <div
                            className={classnames(
                                'flex shrink-0 items-center gap-2',
                                reserveNotesFab && 'mr-24 sm:mr-28'
                            )}
                        >
                            {actions}
                        </div>
                    ) : null}
                </div>
            )}
            {showSectionTabs && resolvedTitle != null ? (
                <div className="mt-4 min-w-0">
                    {typeof resolvedTitle === 'string' ? (
                        <h1 className="m-0 text-2xl font-medium leading-none text-slate-900 dark:text-white">
                            {resolvedTitle}
                        </h1>
                    ) : (
                        <div className="text-slate-900 dark:text-white">{resolvedTitle}</div>
                    )}
                </div>
            ) : null}
            {subtitle != null && subtitle !== '' ? (
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</div>
            ) : null}
        </div>
    )
}
